import { AlertCircle, RefreshCw, WifiOff, ServerCrash } from 'lucide-react'
import { Button } from './Button'
import { useTranslation } from '../../hooks/useTranslation'

interface ApiErrorProps {
  error: Error | null
  onRetry?: () => void
  message?: string
  className?: string
  compact?: boolean
}

export function ApiError({
  error,
  onRetry,
  message,
  className = '',
  compact = false,
}: ApiErrorProps) {
  const { t } = useTranslation()

  // Determine error type and appropriate message
  const getErrorInfo = () => {
    if (!error) {
      return {
        icon: AlertCircle,
        title: t('errors.unknown'),
        description: message || t('errors.tryAgain'),
      }
    }

    const errorMessage = error.message.toLowerCase()

    // Network error
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return {
        icon: WifiOff,
        title: t('errors.network'),
        description: t('errors.checkConnection'),
      }
    }

    // Server error (5xx)
    if (errorMessage.includes('500') || errorMessage.includes('server')) {
      return {
        icon: ServerCrash,
        title: t('errors.server'),
        description: t('errors.tryAgainLater'),
      }
    }

    // Not found (404)
    if (errorMessage.includes('404') || errorMessage.includes('not found')) {
      return {
        icon: AlertCircle,
        title: t('errors.notFound'),
        description: message || t('errors.resourceNotFound'),
      }
    }

    // Default error
    return {
      icon: AlertCircle,
      title: t('errors.error'),
      description: message || error.message || t('errors.tryAgain'),
    }
  }

  const { icon: Icon, title, description } = getErrorInfo()

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg ${className}`}>
        <Icon className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-300 truncate">{description}</p>
        </div>
        {onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            className="text-red-400 hover:text-red-300 flex-shrink-0"
          >
            {t('common.retry')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}>
      <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-center max-w-md mb-6">{description}</p>
      {onRetry && (
        <Button
          variant="secondary"
          onClick={onRetry}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          {t('common.retry')}
        </Button>
      )}
    </div>
  )
}

// Inline error for tables or lists
interface InlineErrorProps {
  message?: string
  onRetry?: () => void
}

export function InlineError({ message = 'Failed to load data', onRetry }: InlineErrorProps) {
  return (
    <div className="flex items-center justify-center gap-2 py-4 text-red-400">
      <AlertCircle className="w-4 h-4" />
      <span className="text-sm">{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-sm underline hover:text-red-300 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  )
}

export default ApiError
